import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Heading, Label, Text } from '@acb_devel/design-system';
import { Button } from '@acb_devel/design-system';
import { Section } from '@layouts/Section/Section';
import { Field } from '@acb_devel/design-system';
import { Select, SelectItem } from '@acb_devel/design-system';
import { Textarea } from '@acb_devel/design-system';
import { Radio, RadioItem } from '@acb_devel/design-system';
import { FileUpload } from '@acb_devel/design-system';
import { Alert } from '@acb_devel/design-system';
import { Checkbox } from '@acb_devel/design-system';
import { ToastRoot } from '@acb_devel/design-system';
import { useToastContext } from '@acb_devel/design-system';
import { LEGAL_NOTICE } from '@data/constants';

// https://github.com/dr5hn/countries-states-cities-database
import states from '@data/states.json';
import spainCities from '@data/spainCities.json';
import styles from './ProfileForm.module.scss';
import {
  UserProfile,
  EnglishLevel,
  uploadProfilePicture,
} from '@services/profiles';
import type { User } from '@services/users';
import { getEvents } from '@services/events';
import type { Event } from '@services/events';

export interface UserProfileForm extends UserProfile {
  event: null | string;
  areas: null | string[];
}

interface StatesType {
  id: number;
  name: string;
  country_id: number;
  country_code: string;
  country_name: string;
}

interface CityType {
  id: number;
  name: string;
  state_id: number;
}

export function ProfileForm({
  onSubmit,
  defaultValues,
}: {
  onSubmit: (profile: UserProfileForm) => void;
  defaultValues?: User;
}) {
  const [step, setStep] = useState(1);
  const [events, setEvents] = useState([] as Event[]);
  const [selectedEvent, setSelectedEvent] = useState(null as Event | null);
  const [selectedFile, setSelectedFile] = useState<string[]>([]);
  const { addToast } = useToastContext();

  function nextStep() {
    window.scrollTo(0, 0);
    setStep(step + 1);
  }
  function prevStep() {
    window.scrollTo(0, 0);
    setStep(step - 1);
  }

  const useFormParams = { defaultValues: {} };

  if (defaultValues) {
    useFormParams.defaultValues = {
      email: defaultValues.emailAddress,
    };
  }

  if (defaultValues?.profile) {
    useFormParams.defaultValues = {
      firstName: defaultValues.profile.firstName,
      lastName: defaultValues.profile.lastName,
      email: defaultValues.emailAddress,
      dateOfBirth: defaultValues.profile.dateOfBirth,
      province: defaultValues.profile.province,
      city: defaultValues.profile.city,
      identificationNumber: defaultValues.profile.identificationNumber,
      phoneNumber: defaultValues.profile.phoneNumber,
      instagram: defaultValues.profile.instagram,
      xTwitter: defaultValues.profile.xTwitter,
      tiktok: defaultValues.profile.tiktok,
      skype: defaultValues.profile.skype,
      occupation: defaultValues.profile.occupation,
      education: defaultValues.profile.education,
      previousExperience: defaultValues.profile.previousExperience,
      englishLevel: defaultValues.profile.englishLevel,
      motivation: defaultValues.profile.motivation,
      relationWithBasketball: defaultValues.profile.relationWithBasketball,
      howDidYouFindUs: defaultValues.profile.howDidYouFindUs,
      profilePicturePath: defaultValues.profile.profilePicturePath,
      profilePictureFullUrl: defaultValues.profile.profilePictureFullUrl,
      shirtSize: defaultValues.profile.shirtSize,
      pantsSize: defaultValues.profile.pantsSize,
      informAboutFutureEvents: defaultValues.profile.informAboutFutureEvents
        ? 'yes'
        : 'no',
      dataPolicyAccepted: defaultValues.profile.dataPolicyAccepted,
    };
  }

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UserProfileForm>(useFormParams);

  function onSubmitWrapper(event: React.FormEvent) {
    event.preventDefault();

    handleSubmit((data) => {
      onSubmit(data);
    })();
  }

  const selectedState = watch('province');
  const selectedCity = watch('city');
  const selectedEventId = watch('event');
  const profilePictureFullUrl = watch('profilePictureFullUrl');

  useEffect(() => {
    if (cities.find((city: any) => city.name === selectedCity)) {
      setValue('city', selectedCity);
    } else {
      setValue('city', null);
    }
  }, [selectedState, setValue]);

  const cities = useMemo(
    () => spainCities.filter((city: any) => city.state_name === selectedState),
    [selectedState]
  );

  useEffect(() => {
    getEvents({
      open_for_registration: true,
    })
      .then(({ results }) => {
        setEvents(results);
      })
      .catch((error) => {
        console.error('Error fetching events', error);
      });
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      const selectedEvent = events.find(
        (event) => event.id === parseInt(selectedEventId)
      );

      if (!selectedEvent) {
        return;
      }

      setSelectedEvent(selectedEvent);
    }
  }, [selectedEventId, events]);

  const onChangeProfilePicture = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    uploadProfilePicture(file)
      .then((response) => {
        setValue('profilePicturePath', response.storagePath);
        setValue('profilePictureFullUrl', response.storageUrl);
      })
      .catch((error) => {
        addToast(
          <ToastRoot>
            <Alert
              variant="error"
              direction="column"
              title={`Error al subir la foto de perfil`}
              text="Ha ocurrido un error al subir tu foto de perfil. Asegúrate que el fichero cumpla el formato y tamaño permitido (5 MB)."
            />
          </ToastRoot>
        );
        console.error('Error uploading profile picture', error);
      });
  };

  useEffect(() => {
    if (profilePictureFullUrl) {
      setSelectedFile([profilePictureFullUrl]);
    }
  }, [profilePictureFullUrl]);

  return (
    <Section className={styles.registerForm}>
      <div className={styles.registerForm__steps}>
        <div
          className={styles.registerForm__step}
          data-active={step === 1}
          data-completed={step > 1}
        >
          <Heading
            as="p"
            variant="heading-2"
            className={styles.registerForm__title}
          >
            Datos básicos
          </Heading>
        </div>
        <div
          className={styles.registerForm__step}
          data-active={step === 2}
          data-completed={step > 2}
        >
          <Heading
            as="p"
            variant="heading-2"
            className={styles.registerForm__title}
          >
            Sobre tí
          </Heading>
        </div>
        <div
          className={styles.registerForm__step}
          data-active={step === 3}
          data-completed={step > 3}
        >
          <Heading
            as="p"
            variant="heading-2"
            className={styles.registerForm__title}
          >
            Dónde participar
          </Heading>
        </div>
      </div>
      <div className={styles.registerForm__alignEnd}>
        <Text
          variant="body-2"
          className={styles.registerForm__requiredAclaration}
        >
          <span className={styles.registerForm__requiredAclarationAsterisk}>
            *
          </span>{' '}
          Estos campos son obligatorios
        </Text>
      </div>
      <form className={styles.registerForm__form} onSubmit={onSubmitWrapper}>
        {step === 1 && (
          <>
            <div className={styles.registerForm__formCard}>
              <div className={styles.registerForm__namesContainer}>
                <Field
                  id="name"
                  type="text"
                  label="Nombre"
                  required
                  {...register('firstName', {
                    required: 'El nombre es obligatorio.',
                    maxLength: {
                      value: 100,
                      message:
                        'El nombre no puede tener más de 100 caracteres.',
                    },
                  })}
                  errorMessage={errors.firstName?.message}
                />
                <Field
                  id="surname"
                  type="text"
                  label="Apellidos"
                  required
                  {...register('lastName', {
                    required: 'El apellido es obligatorio.',
                    maxLength: {
                      value: 250,
                      message:
                        'El apellido no puede tener más de 250 caracteres.',
                    },
                  })}
                  errorMessage={errors.lastName?.message}
                />
              </div>
              <Field
                id="email"
                type="email"
                label="Correo electrónico"
                placeholder="tuemail@gmail.com"
                required
                className={styles.registerForm__halfWidth}
                {...register('email')}
                disabled
              />
              <Field
                id="birthdate"
                type="date"
                label="Fecha de nacimiento"
                placeholder="dd/mm/aaaa"
                required
                className={styles.registerForm__halfWidth}
                {...register('dateOfBirth', {
                  required: 'La fecha de nacimiento es obligatoria.',
                  validate: (value) => {
                    const date = new Date(value);
                    const currentDate = new Date();
                    const minAge = 17;
                    const maxAge = 100;
                    const minDate = new Date(
                      currentDate.getFullYear() - minAge,
                      currentDate.getMonth(),
                      currentDate.getDate()
                    );
                    const maxDate = new Date(
                      currentDate.getFullYear() - maxAge,
                      currentDate.getMonth(),
                      currentDate.getDate()
                    );

                    if (date > minDate) {
                      return `Debes tener al menos ${minAge} años.`;
                    }

                    if (date < maxDate) {
                      return `Debes tener menos de ${maxAge} años.`;
                    }

                    return true;
                  },
                })}
                errorMessage={errors.dateOfBirth?.message}
              />
              <div className={styles.registerForm__halfWidthContainer}>
                <Controller
                  name="province"
                  control={control}
                  rules={{ required: 'La región es obligatoria.' }}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      {...field}
                      id="state"
                      label="Provincia"
                      placeholder="Seleccionar"
                      required
                      errorMessage={errors.province?.message}
                    >
                      {states.map((state: StatesType) => (
                        <SelectItem key={state.id} value={state.name}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </Select>
                  )}
                />
                <Controller
                  name="city"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!value && cities.length > 0) {
                        return 'Selecciona una ciudad.';
                      }
                    },
                  }}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      {...field}
                      id="city"
                      label="Ciudad"
                      placeholder={
                        !selectedState || cities.length === 0
                          ? '-'
                          : 'Seleccionar'
                      }
                      required
                      disabled={!selectedState || cities.length === 0}
                      errorMessage={errors.city?.message}
                      value={field.value ?? undefined}
                    >
                      {cities.map((city: CityType) => (
                        <SelectItem key={city.id} value={city.name}>
                          {city.name}
                        </SelectItem>
                      ))}
                    </Select>
                  )}
                />
              </div>
              <hr className={styles.registerForm__separator} />
              <Field
                id="identificationNumber"
                type="text"
                label="DNI, NIE o Pasaporte"
                required
                className={styles.registerForm__halfWidth}
                errorMessage={errors.identificationNumber?.message}
                {...register('identificationNumber', {
                  required: 'El DNI es obligatorio.',
                  minLength: {
                    value: 7,
                    message:
                      'El formato del documento de identidad no es válido.',
                  },
                  maxLength: {
                    value: 10,
                    message:
                      'El formato del documento de identidad no es válido.',
                  },
                })}
              />
              <Field
                id="phone"
                type="tel"
                label="Teléfono"
                required
                className={styles.registerForm__halfWidth}
                errorMessage={errors.phoneNumber?.message}
                {...register('phoneNumber', {
                  required: 'El teléfono es obligatorio.',
                  minLength: {
                    value: 9,
                    message: 'El teléfono debe tener al menos 9 caracteres.',
                  },
                  maxLength: {
                    value: 15,
                    message: 'El teléfono no puede tener más de 15 caracteres.',
                  },
                  pattern: {
                    value: /^\+?[0-9]+$/,
                    message: 'El teléfono no es válido.',
                  },
                })}
              />
            </div>
            <div className={styles.registerForm__formCard}>
              <div className={styles.registerForm__halfWidthContainer}>
                <Field
                  id="instagram"
                  type="text"
                  label="Instagram"
                  {...register('instagram', {
                    maxLength: {
                      value: 100,
                      message:
                        'El nombre de usuario no puede tener más de 100 caracteres.',
                    },
                  })}
                />
                <Field
                  id="twitter"
                  type="text"
                  label="X (Twitter)"
                  {...register('xTwitter', {
                    maxLength: {
                      value: 100,
                      message:
                        'El nombre de usuario no puede tener más de 100 caracteres.',
                    },
                  })}
                />
              </div>
              <div className={styles.registerForm__halfWidthContainer}>
                <Field
                  id="tiktok"
                  type="text"
                  label="TikTok"
                  className={styles.registerForm__halfWidth}
                  {...register('tiktok', {
                    maxLength: {
                      value: 100,
                      message:
                        'El nombre de usuario no puede tener más de 100 caracteres.',
                    },
                  })}
                />
                <Field
                  id="skype"
                  type="skype"
                  label="Skype"
                  className={styles.registerForm__halfWidth}
                  {...register('skype', {
                    maxLength: {
                      value: 100,
                      message:
                        'El nombre de usuario no puede tener más de 100 caracteres.',
                    },
                  })}
                />
              </div>
              <Text variant="small">
                Si nos facilitas esta información la utilizaremos exclusivamente
                para generar y difundir contenido en las plataformas
                seleccionadas, permitiendo así una mayor interacción y
                visibilidad de la participación en el evento y mensajes. Se
                garantiza que la información será tratada de manera confidencial
                y se utilizará únicamente con este propósito, respetando siempre
                la privacidad de los individuos y cumpliendo con las normativas
                vigentes en materia de protección de datos. Podrás ejercer tus
                derechos de acceso, rectificación, supresión, limitación,
                portabilidad y oposición enviando un correo a dpd@acb.es
              </Text>
            </div>
            <div className={styles.registerForm__actions}>
              {defaultValues?.profile ? (
                <Link to="/user/profile" className={styles.registerForm__link}>
                  <Button
                    onClick={prevStep}
                    variant="outline"
                    size="xl"
                    className={styles.registerForm__button}
                    type="button"
                  >
                    Volver
                  </Button>
                </Link>
              ) : (
                <div></div>
              )}
              <Button
                onClick={handleSubmit(nextStep)}
                size="xl"
                className={styles.registerForm__button}
              >
                Continuar
              </Button>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <div className={styles.registerForm__formCard}>
              <div>
                <Field
                  id="occupation"
                  type="text"
                  label="Ocupación actual"
                  placeholder="Ej: Estudiante, Profesor, etc."
                  required
                  className={styles.registerForm__halfWidth}
                  {...register('occupation', {
                    required: 'La profesión es obligatoria.',
                    maxLength: {
                      value: 150,
                      message:
                        'La profesión no puede tener más de 150 caracteres.',
                    },
                  })}
                  errorMessage={errors.occupation?.message}
                />
                <Text
                  variant="small"
                  className={styles.registerForm__smallText}
                >
                  Si contestas esta casilla nos ayudarás a mejorar la
                  correspondencia entre tus habilidades y las demandas de las
                  diversas áreas del evento. La información recolectada será
                  tratada de manera confidencial y utilizada exclusivamente para
                  esta finalidad. Podrás ejercer tus derechos de acceso,
                  rectificación, supresión, limitación, portabilidad y oposición
                  enviando un correo a dpd@acb.es
                </Text>
              </div>
              <Controller
                name="education"
                control={control}
                rules={{
                  required: 'La formación es obligatoria.',
                  maxLength: {
                    value: 500,
                    message:
                      'La formación no puede tener más de 500 caracteres.',
                  },
                }}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    id="education"
                    label="Formación"
                    placeholder="Indica cuáles son tus estudios y en qué te has especializado. Ej: ciclo formativo de sistemas informáticos"
                    required
                    maxLength={500}
                    errorMessage={errors.education?.message}
                  />
                )}
              />
              <Controller
                name="previousExperience"
                control={control}
                rules={{
                  required: 'La experiencia es obligatoria.',
                  maxLength: {
                    value: 500,
                    message:
                      'La experiencia no puede tener más de 500 caracteres.',
                  },
                }}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    id="previousExperience"
                    label="Experiencia previa en eventos deportivos. En caso afirmativo indica los eventos y funciones realizadas"
                    placeholder="Ej: Copa del Rey 2024 Málaga"
                    required
                    maxLength={500}
                    errorMessage={errors.previousExperience?.message}
                  />
                )}
              />
              <Controller
                name="englishLevel"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={field.onChange}
                    {...field}
                    id="englishLevel"
                    label="Nivel de inglés"
                    placeholder="Seleccionar"
                    className={styles.registerForm__halfWidth}
                  >
                    <SelectItem value={EnglishLevel.BASIC}>Básico</SelectItem>
                    <SelectItem value={EnglishLevel.INTERMEDIATE}>
                      Intermedio
                    </SelectItem>
                    <SelectItem value={EnglishLevel.ADVANCED}>
                      Avanzado
                    </SelectItem>
                    <SelectItem value={EnglishLevel.NATIVE}>Nativo</SelectItem>
                  </Select>
                )}
              />
            </div>
            <div className={styles.registerForm__formCard}>
              <Controller
                name="motivation"
                control={control}
                rules={{
                  required: 'Indica tu motivación.',
                  maxLength: {
                    value: 500,
                    message:
                      'La motivación no puede tener más de 500 caracteres.',
                  },
                }}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    id="motivation"
                    label="¿Por qué quieres ser voluntario?"
                    placeholder="Ej: Me apasiona el baloncesto"
                    required
                    maxLength={500}
                    errorMessage={errors.motivation?.message}
                  />
                )}
              />
              <Controller
                name="relationWithBasketball"
                control={control}
                rules={{
                  required: 'Indica tu relación con el baloncesto.',
                  maxLength: {
                    value: 500,
                    message:
                      'La relación no puede tener más de 500 caracteres.',
                  },
                }}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    id="relationWithBasketball"
                    label="¿Qué relación tienes con el baloncesto?"
                    placeholder="Ej: He jugado desde pequeño"
                    required
                    maxLength={500}
                    errorMessage={errors.relationWithBasketball?.message}
                  />
                )}
              />
              {!defaultValues?.profile && (
                <Controller
                  name="howDidYouFindUs"
                  control={control}
                  rules={{
                    required: 'Selecciona una opción.',
                    maxLength: {
                      value: 250,
                      message:
                        'No puedes tener más de 250 caracteres en este campo.',
                    },
                  }}
                  render={({ field }) => {
                    let customValue = '';
                    const options = [
                      { value: 'ACB.COM', label: 'ACB.COM' },
                      { value: 'Redes sociales', label: 'Redes sociales' },
                      { value: 'Amigos', label: 'Amigos' },
                      {
                        value: 'Federación y clubes',
                        label: 'Federación y clubes',
                      },
                      {
                        value: 'Medios de comunicación',
                        label: 'Medios de comunicación',
                      },
                    ];

                    const matchedValue = options.find(
                      (option) => option.value === field.value
                    );

                    if (!matchedValue && field.value) {
                      customValue = field.value;
                    }

                    return (
                      <Radio
                        id="howDidYouFindUs"
                        label="¿Cómo nos conociste?"
                        required
                        errorMessage={errors.howDidYouFindUs?.message}
                        onValueChange={field.onChange}
                        {...field}
                      >
                        {options.map((option) => (
                          <RadioItem
                            id={`howDidYouFindUs_${option.value}`}
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </RadioItem>
                        ))}
                        <RadioItem
                          id="howDidYouFindUs_other"
                          value={customValue}
                          customOption
                          onValueChange={field.onChange}
                        >
                          Otro
                        </RadioItem>
                      </Radio>
                    );
                  }}
                />
              )}
            </div>
            <div className={styles.registerForm__formCard}>
              <div className={styles.registerForm__clarificationText}>
                <Label htmlFor="fr">Tu foto de perfil</Label>
                <Text variant="body-2">
                  Por favor, sube una foto tipo carnet, en la que tu rostro sea
                  claramente reconocible. Asegúrate de que tenga buena calidad y
                  esté bien iluminada.
                </Text>
                <FileUpload
                  id="profilePicture"
                  label=""
                  clarificationText="Adjuntar la imagen en formato jpg o png y con un tamaño máximo de 5MB"
                  accept="image/*"
                  onChange={onChangeProfilePicture}
                  onDelete={() => {
                    setValue('profilePicturePath', '');
                    setValue('profilePictureFullUrl', '');
                  }}
                  buttonText="Adjuntar foto"
                  buttonUpperCase={false}
                  value={selectedFile}
                  setValue={setSelectedFile}
                />
              </div>
            </div>
            <div className={styles.registerForm__actions}>
              <Button
                onClick={prevStep}
                variant="outline"
                size="xl"
                className={styles.registerForm__button}
                type="button"
              >
                Paso anterior
              </Button>
              <Button
                onClick={handleSubmit(nextStep)}
                size="xl"
                className={styles.registerForm__button}
              >
                Continuar
              </Button>
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <div className={styles.registerForm__formCard}>
              <div className={styles.registerForm__uniformSizeTitle}>
                <Heading as="p" variant="heading-2">
                  Talla Uniforme Voluntariado
                </Heading>
                <Text variant="body-2">Tallaje sujeto a disponibilidad</Text>
              </div>
              <div className={styles.registerForm__halfWidthContainer}>
                <Controller
                  name="shirtSize"
                  control={control}
                  rules={{ required: 'Selecciona una opción.' }}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      {...field}
                      id="shirtSize"
                      label="Polo y sudadera"
                      placeholder="Seleccionar"
                      className={styles.registerForm__halfWidth}
                      errorMessage={errors.shirtSize?.message}
                    >
                      <SelectItem value="xs">XS</SelectItem>
                      <SelectItem value="s">S</SelectItem>
                      <SelectItem value="m">M</SelectItem>
                      <SelectItem value="l">L</SelectItem>
                      <SelectItem value="xl">XL</SelectItem>
                      <SelectItem value="2xl">2XL</SelectItem>
                      <SelectItem value="3xl">3XL</SelectItem>
                      <SelectItem value="4xl">4XL</SelectItem>
                    </Select>
                  )}
                />
                <Controller
                  name="pantsSize"
                  control={control}
                  rules={{ required: 'Selecciona una opción.' }}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      {...field}
                      id="pantsSize"
                      label="Pantalón"
                      placeholder="Seleccionar"
                      className={styles.registerForm__halfWidth}
                      errorMessage={errors.pantsSize?.message}
                    >
                      <SelectItem value="xs">XS</SelectItem>
                      <SelectItem value="s">S</SelectItem>
                      <SelectItem value="m">M</SelectItem>
                      <SelectItem value="l">L</SelectItem>
                      <SelectItem value="xl">XL</SelectItem>
                      <SelectItem value="2xl">2XL</SelectItem>
                      <SelectItem value="3xl">3XL</SelectItem>
                      <SelectItem value="4xl">4XL</SelectItem>
                    </Select>
                  )}
                />
              </div>
            </div>
            <div className={styles.registerForm__formCard}>
              {!defaultValues?.profile && (
                <>
                  <div className={styles.registerForm__eventsContainer}>
                    <Label htmlFor="event" required>
                      Evento en el que deseas incribirte
                    </Label>
                    {events.length === 0 ? (
                      <Alert
                        variant="warning"
                        direction="row"
                        title="No hay eventos disponibles"
                        text="Te apuntamos en nuestra base de datos"
                      />
                    ) : (
                      <>
                        <Controller
                          name="event"
                          control={control}
                          rules={{
                            required: 'Selecciona un evento.',
                          }}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              {...field}
                              id="event"
                              placeholder="Selecciona un evento"
                              errorMessage={errors.event?.message}
                              value={field.value ?? undefined}
                            >
                              {events.map((event) => (
                                <SelectItem
                                  key={event.id}
                                  value={event.id.toString()}
                                >
                                  {event.fullName}
                                </SelectItem>
                              ))}
                            </Select>
                          )}
                        />
                        {selectedEvent &&
                          selectedEvent?.eventAreas?.length > 0 && (
                            <Controller
                              name="areas"
                              control={control}
                              rules={{
                                required: 'Selecciona al menos un área.',
                              }}
                              render={({ field }) => (
                                <>
                                  <div
                                    className={
                                      styles.registerForm__areasListContainer
                                    }
                                  >
                                    <Label htmlFor="areas" required>
                                      ¿En qué áreas preferirías participar?
                                    </Label>
                                    <Text variant="body-2">
                                      Tus preferencias serán tenidas en cuenta,
                                      y la asignación de área se basará en tu
                                      perfil, características, disponibilidad y
                                      preferencias personales,{' '}
                                      <b>
                                        pero no se garantiza que el área
                                        asignada esté entre las elegidas.
                                      </b>
                                    </Text>
                                    {errors.areas && (
                                      <Alert
                                        variant="error"
                                        title="Selecciona al menos un área."
                                      />
                                    )}
                                    <div
                                      className={styles.registerForm__areasList}
                                    >
                                      {selectedEvent?.eventAreas?.map(
                                        (area) => (
                                          <Checkbox
                                            key={area.id}
                                            id={`area-${area.id}`}
                                            label={area.name}
                                            errorMessage={
                                              errors.areas ? ' ' : ''
                                            }
                                            onCheckedChange={(checked) => {
                                              const currentValues =
                                                field.value ?? [];

                                              if (checked) {
                                                field.onChange([
                                                  ...currentValues,
                                                  area.id,
                                                ]);
                                              } else {
                                                field.onChange(
                                                  currentValues.filter(
                                                    (selectedArea) =>
                                                      selectedArea.toString() !==
                                                      area.id.toString()
                                                  )
                                                );
                                              }
                                            }}
                                          />
                                        )
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}
                            />
                          )}
                      </>
                    )}
                  </div>
                  <hr className={styles.registerForm__separator} />
                </>
              )}
              <Controller
                name="informAboutFutureEvents"
                control={control}
                rules={{ required: 'Selecciona una opción.' }}
                render={({ field }) => (
                  <Radio
                    id="informAboutFutureEvents"
                    label="¿Quieres que te informemos de futuras actividades de voluntariado?"
                    onValueChange={field.onChange}
                    {...field}
                    errorMessage={errors.informAboutFutureEvents?.message}
                  >
                    <RadioItem id="informAboutFutureEvents_yes" value="yes">
                      Sí
                    </RadioItem>
                    <RadioItem id="informAboutFutureEvents_no" value="no">
                      No
                    </RadioItem>
                  </Radio>
                )}
              />
              {!defaultValues?.profile && (
                <>
                  <hr className={styles.registerForm__separator} />
                  <div className={styles.registerForm__termsContainer}>
                    <Label htmlFor="dataPolicyAccepted" required>
                      Información básica sobre Protección de Datos
                    </Label>
                    <Controller
                      name="dataPolicyAccepted"
                      control={control}
                      rules={{
                        required:
                          'Para continuar, debes aceptar los términos y condiciones.',
                      }}
                      render={({ field }) => (
                        <Checkbox
                          id="dataPolicyAccepted"
                          // label="Acepto los términos y condiciones"
                          label={
                            <Text variant="body-2">
                              Acepto la{' '}
                              <Link
                                to={LEGAL_NOTICE}
                                target="_blank"
                                className={styles.registerForm__link}
                              >
                                política de protección de datos
                              </Link>
                            </Text>
                          }
                          onCheckedChange={field.onChange}
                          checked={field.value}
                          {...field}
                          errorMessage={errors.dataPolicyAccepted?.message}
                        />
                      )}
                    />
                  </div>
                </>
              )}
            </div>
            <div className={styles.registerForm__actions}>
              <Button
                onClick={prevStep}
                variant="outline"
                size="xl"
                className={styles.registerForm__button}
                type="button"
              >
                Paso anterior
              </Button>
              <Button
                type="submit"
                size="xl"
                className={styles.registerForm__button}
              >
                Finalizar
              </Button>
            </div>
          </>
        )}
      </form>
    </Section>
  );
}
